import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowUpRight, CheckCircle2, ChevronDown, ExternalLink, FileUp, Info, Loader2, Trash2, Upload } from 'lucide-react';
import { iccManager } from '../lib/icc/iccManager';
import type { ICCProfileDescriptor, ICCProfileSnapshot, RenderingIntent } from '../lib/icc/types';
import type { ColorResult, PrintProofMetrics, PrintSuggestion } from '../lib/colorEngine';
import { InfoIconTooltip } from './Tooltip';
import { educationalContent } from '../lib/educationalContent';
import { useToolState } from '../contexts/ToolStateContext';

const MAX_UPLOAD_HINT = 'Files larger than 10MB are rejected to keep processing responsive.';

const RENDERING_INTENT_OPTIONS: { id: RenderingIntent; label: string; description: string }[] = [
  {
    id: 'perceptual',
    label: 'Perceptual',
    description: 'Balances contrast and saturation for photographic content.',
  },
  {
    id: 'relative',
    label: 'Relative Colorimetric',
    description: 'Preserves in-gamut colors, clipping out-of-gamut values.',
  },
  {
    id: 'absolute',
    label: 'Absolute Colorimetric',
    description: 'Simulates paper white for proofing workflows.',
  },
  {
    id: 'saturation',
    label: 'Saturation',
    description: 'Maximises vividness for charts or infographics.',
  },
];

function formatBytes(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(timestamp: number) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toLocaleString();
  }
}

function useIccSnapshot() {
  const [snapshot, setSnapshot] = useState<ICCProfileSnapshot>(() => iccManager.getSnapshot());

  useEffect(() => {
    return iccManager.subscribe((state) => {
      setSnapshot(state);
    });
  }, []);

  return snapshot;
}

interface ProfileCardProps {
  profile: ICCProfileDescriptor;
  active: boolean;
  onSelect: (profile: ICCProfileDescriptor) => void;
  onRemove: (profile: ICCProfileDescriptor) => void;
  onUpdate: (profile: ICCProfileDescriptor, updates: Partial<Pick<ICCProfileDescriptor, 'renderingIntent' | 'blackPointCompensation'>>) => void;
  metrics?: PrintProofMetrics;
  sourceHex?: string;
}

function ProofSwatch({ label, hex, copiedField, fieldKey, onCopy, onSetActive }: {
  label: string;
  hex?: string;
  copiedField?: string | null;
  fieldKey?: string;
  onCopy?: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
}) {
  const isCopied = fieldKey ? copiedField === fieldKey : false;

  return (
    <div className="prt-proof-swatch group">
      <div className="prt-proof-swatch__color" style={{ backgroundColor: hex ?? 'transparent' }} />
      <div className="prt-proof-swatch__info">
        <span className="prt-proof-swatch__label">{label}</span>
        {hex ? (
          <button className="prt-proof-swatch__hex" onClick={() => onCopy?.(hex, fieldKey ?? '')}>
            {isCopied ? 'Copied ✓' : hex}
          </button>
        ) : (
          <span className="prt-proof-swatch__hex prt-proof-swatch__hex--empty">—</span>
        )}
      </div>
      <div className="prt-proof-swatch__actions">
        {hex && (
          <button className="prt-card__action-btn" title="Use as active color" onClick={() => onSetActive?.(hex)}>
            <ArrowUpRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function MetricsSummary({ metrics, sourceHex, copiedField, onCopy, onSetActive }: {
  metrics?: PrintProofMetrics;
  sourceHex?: string;
  copiedField?: string | null;
  onCopy?: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
}) {
  if (!metrics) {
    return <p className="prt-metrics__empty">Convert a color to view TAC and soft-proof metrics for this profile.</p>;
  }

  const { tac, maxChannel, softProofHex, softProofDeltaE, warnings } = metrics;

  return (
    <div className="prt-metrics">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="prt-metrics__label inline-flex items-center gap-1">
            Total area coverage
            <InfoIconTooltip
              content={educationalContent.tac.description}
              learnMoreUrl={educationalContent.tac.learnMoreUrl}
              position="top"
            />
          </span>
          <span className="block font-semibold text-sm">{tac.toFixed(1)}%</span>
        </div>
        <div>
          <span className="prt-metrics__label block">Max channel</span>
          <span className="block font-semibold text-sm">{maxChannel.toFixed(1)}%</span>
        </div>
        <div>
          <span className="prt-metrics__label inline-flex items-center gap-1">
            Soft-proof ΔE2000
            <InfoIconTooltip
              content={educationalContent.deltaE2000.description}
              learnMoreUrl={educationalContent.deltaE2000.learnMoreUrl}
              position="top"
            />
          </span>
          <span className="block font-semibold text-sm">{softProofDeltaE !== undefined ? softProofDeltaE.toFixed(2) : '—'}</span>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <ProofSwatch label="Source" hex={sourceHex} copiedField={copiedField} fieldKey="proof-source" onCopy={onCopy} onSetActive={onSetActive}
        />
        <ProofSwatch label="Proof" hex={softProofHex} copiedField={copiedField} fieldKey="proof-result" onCopy={onCopy} onSetActive={onSetActive}
        />
      </div>
      <div className="space-y-1">
        <span className="prt-metrics__label block">Warnings</span>
        {warnings.length === 0 ? (
          <span className="prt-status-ok">
            <CheckCircle2 className="w-3 h-3" /> Within recommended thresholds
          </span>
        ) : (
          <ul className="space-y-1">
            {warnings.map((warning, index) => (
              <li key={`${warning}-${index}`} className="prt-warning-item">
                <AlertTriangle className="w-3 h-3 mt-0.5" />
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ProfileCard({ profile, active, onSelect, onRemove, onUpdate, metrics, sourceHex }: ProfileCardProps) {
  return (
    <div
      className={`prt-profile-card${active ? ' prt-profile-card--active' : ''}`}
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h4 className="prt-profile-card__title flex items-center gap-2">
            {profile.name}
            {active && (
              <span className="prt-active-badge">
                <CheckCircle2 className="w-3 h-3" /> Active
              </span>
            )}
          </h4>
          <p className="prt-profile-card__subtitle">{profile.fileName}</p>
        </div>
        <button
          type="button"
          onClick={() => onRemove(profile)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
        >
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      </header>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <dt className="prt-profile-card__meta-label">Size</dt>
          <dd className="prt-profile-card__meta-value text-sm">{formatBytes(profile.size)}</dd>
        </div>
        <div>
          <dt className="prt-profile-card__meta-label">Created</dt>
          <dd className="prt-profile-card__meta-text">{formatDate(profile.createdAt)}</dd>
        </div>
      </dl>

      <div className="space-y-2">
        <label className="flex flex-col gap-1">
          <span className="prt-profile-card__meta-label inline-flex items-center gap-1">
            Rendering intent
            <InfoIconTooltip
              content={educationalContent.renderingIntentRelative.description}
              learnMoreUrl={educationalContent.renderingIntentRelative.learnMoreUrl}
              position="right"
            />
          </span>
          <select
            value={profile.renderingIntent}
            onChange={(event) => onUpdate(profile, { renderingIntent: event.target.value as RenderingIntent })}
            className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            {RENDERING_INTENT_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={profile.blackPointCompensation}
            onChange={(event) => onUpdate(profile, { blackPointCompensation: event.target.checked })}
            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          Black-point compensation
          <InfoIconTooltip
            content={educationalContent.blackPointCompensation.description}
            learnMoreUrl={educationalContent.blackPointCompensation.learnMoreUrl}
            position="right"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <button
          type="button"
          onClick={() => onSelect(profile)}
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-md border text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
            active
              ? 'prt-suggestion--active'
              : ''
          }`}
        >
          <Info className="w-3 h-3" /> {active ? 'Using in conversions' : 'Set as active'}
        </button>
      </div>

      <MetricsSummary metrics={metrics} sourceHex={sourceHex} />
    </div>
  );
}

interface PrintToolsPanelProps {
  result: ColorResult | null;
  selectedProfileId: string;
  onSelectProfile: (profileId: string) => void;
  copiedField?: string | null;
  onCopy?: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
}

export function PrintToolsPanel({ result, selectedProfileId, onSelectProfile, copiedField, onCopy, onSetActive }: PrintToolsPanelProps) {
  const snapshot = useIccSnapshot();
  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('print');

  const [defaultIntent, setDefaultIntent] = useState<RenderingIntent>('relative');
  const [defaultBlackPoint, setDefaultBlackPoint] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Restore persisted profile selection
  useEffect(() => {
    if (persisted?.selectedProfileId && persisted.selectedProfileId !== selectedProfileId) {
      onSelectProfile(persisted.selectedProfileId);
    }
  // Only run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist profile selection on change
  useEffect(() => {
    setToolState('print', { selectedProfileId });
  }, [selectedProfileId, setToolState]);

  useEffect(() => {
    if (snapshot.error) {
      // Defer state update to avoid synchronous setState in effect
      setTimeout(() => {
        setError(snapshot.error);
      }, 0);
    }
  }, [snapshot.error, snapshot.revision]);

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setMessage(null);
    try {
      const descriptor = await iccManager.uploadProfile(file, {
        renderingIntent: defaultIntent,
        blackPointCompensation: defaultBlackPoint,
      });
      setMessage(`${descriptor.name} ready for use.`);
    } catch (uploadError) {
      const messageText = uploadError instanceof Error ? uploadError.message : 'Failed to import ICC profile.';
      setError(messageText);
    }
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    await handleUpload(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0] ?? null;
    await handleUpload(file);
  };

  const clearNotifications = () => {
    setMessage(null);
    setError(null);
  };

  const handleSelect = (profile: ICCProfileDescriptor) => {
    onSelectProfile(profile.pluginId);
    setMessage(`${profile.name} set as active.`);
  };

  const handleApplySuggestion = (suggestion: PrintSuggestion) => {
    onSelectProfile(suggestion.profileId);
    setMessage(`${suggestion.profileLabel} applied from nearest printable suggestions.`);
  };

  const handleRemove = (profile: ICCProfileDescriptor) => {
    iccManager.removeProfile(profile.id);
    setMessage(`${profile.name} removed.`);
    if (selectedProfileId === profile.pluginId) {
      onSelectProfile('generic');
    }
  };

  const handleUpdate = (
    profile: ICCProfileDescriptor,
    updates: Partial<Pick<ICCProfileDescriptor, 'renderingIntent' | 'blackPointCompensation'>>,
  ) => {
    try {
      iccManager.updateProfile(profile.id, updates);
      setMessage(`${profile.name} updated.`);
    } catch (updateError) {
      const messageText = updateError instanceof Error ? updateError.message : 'Unable to update profile.';
      setError(messageText);
    }
  };

  const activeDescriptor = useMemo(
    () => snapshot.profiles.find((profile) => profile.pluginId === snapshot.activeProfileId) ?? null,
    [snapshot.activeProfileId, snapshot.profiles],
  );

  const profileMetrics = useMemo(() => {
    if (!result) {
      return new Map<string, PrintProofMetrics>();
    }
    const map = new Map<string, PrintProofMetrics>();
    for (const profile of result.cmykProfiles) {
      map.set(profile.id, profile.metrics);
    }
    return map;
  }, [result]);

  const genericMetrics = result?.cmykMetrics ?? null;

  const sourceHex = result?.hex;

  const suggestions = useMemo(() => result?.printSuggestions ?? [], [result]);
  const topSuggestions = suggestions.slice(0, 3);

  return (
    <section className="prt-section">
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold inline-flex items-center gap-2" style={{ color: 'var(--ink)' }}>
            Print & ICC Tools
            <InfoIconTooltip
              content={educationalContent.iccProfile.description}
              learnMoreUrl={educationalContent.iccProfile.learnMoreUrl}
              position="right"
            />
          </h3>
          <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
            Upload ICC profiles, configure rendering intents, and route conversions through LittleCMS/WASM.
          </p>
        </div>
        {snapshot.status === 'loading' ? (
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading profile…
          </span>
        ) : activeDescriptor ? (
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-300">
            <Info className="w-4 h-4" /> Active: {activeDescriptor.name}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 text-xs text-gray-500">
            <Info className="w-4 h-4" /> Defaulting to sRGB
          </span>
        )}
      </header>

      {message && (
        <div className="prt-banner prt-banner--success">
          <CheckCircle2 className="w-5 h-5 mt-0.5" />
          <div className="flex-1">
            <p>{message}</p>
            <button type="button" className="text-xs underline" onClick={clearNotifications}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="prt-banner prt-banner--error">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div className="flex-1">
            <p>{error}</p>
            <button type="button" className="text-xs underline" onClick={clearNotifications}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-3 text-center space-y-2 bg-gray-50 dark:bg-gray-800/40"
        style={{ minHeight: '120px', overflow: 'visible' }}
      >
        <FileUp className="w-8 h-8 mx-auto text-gray-400" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Drop ICC/ICM file here</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Or browse for a profile to import.</p>
        </div>
        <div className="flex flex-col items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <label
            htmlFor="icc-upload-input"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
          >
            <Upload className="w-4 h-4" /> Select ICC profile
          </label>
          <div className="prt-upload__defaults">
            <span className="prt-upload__defaults-label">
              Defaults for new imports
              <InfoIconTooltip content="These settings apply only to newly uploaded ICC profiles. They do not change existing profiles or the Device-Derived Proof." position="top" />
            </span>
            <div className="prt-upload__defaults-controls">
              <select
                value={defaultIntent}
                onChange={(event) => setDefaultIntent(event.target.value as RenderingIntent)}
                className="px-3 py-2 text-xs border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900"
              >
                {RENDERING_INTENT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className="inline-flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={defaultBlackPoint}
                  onChange={(event) => setDefaultBlackPoint(event.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                BPC
              </label>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-gray-400">{MAX_UPLOAD_HINT}</p>
        <input
          id="icc-upload-input"
          ref={fileInputRef}
          type="file"
          accept=".icc,.icm"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      {result && (
        <div className="space-y-3">
          <h4 className="prt-heading">Device-derived proof</h4>
          <MetricsSummary metrics={genericMetrics ?? undefined} sourceHex={sourceHex} copiedField={copiedField} onCopy={onCopy} onSetActive={onSetActive} />
        </div>
      )}

      {topSuggestions.length > 0 && (
        <div className="space-y-3">
          <h4 className="prt-heading">Nearest printable colors</h4>
          <ul className="space-y-2">
            {topSuggestions.map((suggestion) => {
              const isActive = selectedProfileId === suggestion.profileId;
              const hexFieldKey = `suggestion-hex-${suggestion.profileId}`;
              return (
                <li
                  key={`${suggestion.profileId}-${suggestion.proofHex ?? 'generic'}`}
                  className={`prt-suggestion${isActive ? ' prt-suggestion--active' : ''}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                        {suggestion.profileLabel}
                      </span>
                      {suggestion.proofHex && (
                        <span className="prt-suggestion__hex-chip group">
                          <span
                            className="prt-suggestion__swatch"
                            style={{ backgroundColor: suggestion.proofHex }}
                          />
                          <button className="prt-suggestion__hex-btn" onClick={(e) => { e.stopPropagation(); onCopy?.(suggestion.proofHex!, hexFieldKey); }}>
                            {copiedField === hexFieldKey ? 'Copied ✓' : suggestion.proofHex}
                          </button>
                          <button className="prt-suggestion__use-btn" onClick={(e) => { e.stopPropagation(); onSetActive?.(suggestion.proofHex!); }}>
                            <ArrowUpRight size={12} />
                          </button>
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleApplySuggestion(suggestion)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold border transition-colors ${
                        isActive
                          ? 'prt-suggestion--active'
                          : ''
                      }`}
                    >
                      {isActive ? 'Active' : 'Use profile'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" style={{ color: 'var(--ink-2)' }}>
                    <div>
                      <span className="prt-metrics__label block">ΔE2000</span>
                      <span className="font-semibold text-sm">{suggestion.deltaE.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="prt-metrics__label block">TAC</span>
                      <span className="font-semibold text-sm">{suggestion.tac.toFixed(1)}%</span>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="prt-metrics__label block">Warnings</span>
                      {suggestion.warnings.length === 0 ? (
                        <span className="prt-status-ok">
                          <CheckCircle2 className="w-3 h-3" /> None detected
                        </span>
                      ) : (
                        <ul className="space-y-1">
                          {suggestion.warnings.map((warning, index) => (
                            <li key={`${suggestion.profileId}-warn-${index}`} className="prt-warning-item">
                              <AlertTriangle className="w-3 h-3 mt-0.5" />
                              <span>{warning}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {result && (
        <details>
          <summary className="prt-details-toggle">
            <ChevronDown size={14} className="prt-details-toggle__icon" />
            Why did my CMYK change?
          </summary>
          <div className="prt-details-content">
            <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1 space-y-1">
              <p className="text-xs leading-relaxed">{educationalContent.whyCmykChanged.description}</p>
              {educationalContent.whyCmykChanged.learnMoreUrl && (
                <a
                  href={educationalContent.whyCmykChanged.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs underline hover:no-underline"
                >
                  Learn more about print workflows <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </details>
      )}

      <div className={`prt-profiles-section${snapshot.profiles.length > 0 ? ' prt-profiles-section--sticky' : ''}`}>
        <h4 className="prt-heading">Loaded profiles</h4>
        {snapshot.profiles.length === 0 ? (
          <p className="text-sm prt-text-muted">
            No ICC profiles loaded. Defaulting to the built-in sRGB conversion.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {snapshot.profiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                active={selectedProfileId === profile.pluginId}
                onSelect={handleSelect}
                onRemove={handleRemove}
                onUpdate={handleUpdate}
                metrics={profileMetrics.get(profile.pluginId)}
                sourceHex={sourceHex}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="prt-footer__note">
          <Info className="w-4 h-4 flex-shrink-0" /> Values shown use approximate conversions. Full ICC profile transforms will be available in a future release.
        </p>
        <p className="prt-footer__note">Upload source and destination ICC profiles to compare conversion intents, TAC limits, and soft-proof deltas side by side.</p>
      </div>
    </section>
  );
}
